import type { PlatformPos, PosEstado } from "@/platform/types";
import { useEstadoDeLaCaja, ESTADO_INICIAL } from "./estadoDeLaCaja";

/**
 * Conecta la pantalla con el latido de la caja (trd-client#344 AC-2). El latido
 * lo manda Rust (`src-tauri/src/pos/heartbeat.rs`); la pantalla:
 *
 * - le dice por qué negocio latir, y lo para al cerrar la sesión;
 * - guarda cada estado que manda, para el indicador y para bloquear la venta;
 * - ante un `401`, hace el refresco de sesión único. Rust no refresca: el
 *   refresh de identity rota, y un segundo uso revoca la sesión;
 * - pide un latido ya cuando el navegador avisa que cambió la red.
 *
 * Hay uno solo por app: la pantalla se vuelve a montar con cada ruta.
 */

export interface DependenciasDelLatido {
  pos: Pick<
    PlatformPos,
    | "startHeartbeat"
    | "stopHeartbeat"
    | "heartbeatNow"
    | "getStatus"
    | "onStatus"
  >;
  bffUrl: string;
  /** El refresco de sesión único de la app (`renewSession`). */
  renovarSesion: () => Promise<void>;
  /** Cuando un latido cambia la baja de la caja: hay que releerla. */
  alCambiarLaBaja?: () => void;
  ventana?: Pick<Window, "addEventListener" | "removeEventListener">;
  ahora?: () => number;
}

/** No se renueva la sesión más de una vez por minuto por el latido. */
export const ENTRE_RENOVACIONES_MS = 60_000;

let actual: { businessId: string; soltar: () => void } | null = null;

const estaDeBaja = (estado: PosEstado) =>
  estado.bloqueo?.tipo === "dada-de-baja";

export async function asegurarLatido(
  businessId: string | null | undefined,
  deps: DependenciasDelLatido
): Promise<void> {
  if (actual && actual.businessId === businessId) return;
  actual?.soltar();
  actual = null;

  if (!businessId) {
    useEstadoDeLaCaja.setState(ESTADO_INICIAL);
    await deps.pos.stopHeartbeat();
    return;
  }

  const ahora = deps.ahora ?? Date.now;
  let renovando = false;
  let ultimaRenovacion = -Infinity;
  let soltado = false;

  const pedirRenovacion = async () => {
    if (renovando || ahora() - ultimaRenovacion < ENTRE_RENOVACIONES_MS) return;
    renovando = true;
    ultimaRenovacion = ahora();
    try {
      await deps.renovarSesion();
      await deps.pos.heartbeatNow();
    } catch {
      // Si no se pudo, la app pide iniciar sesión por su cuenta; el
      // indicador ya dice «Iniciá sesión».
    } finally {
      renovando = false;
    }
  };

  const recibir = (estado: PosEstado) => {
    if (soltado) return;
    const antes = useEstadoDeLaCaja.getState();
    useEstadoDeLaCaja.setState(estado, true);
    if (estaDeBaja(antes) !== estaDeBaja(estado)) deps.alCambiarLaBaja?.();
    if (estado.bloqueo?.tipo === "sesion") void pedirRenovacion();
  };

  const cambioLaRed = () => void deps.pos.heartbeatNow();
  const ventana =
    deps.ventana ?? (typeof window !== "undefined" ? window : undefined);
  ventana?.addEventListener("online", cambioLaRed);
  ventana?.addEventListener("offline", cambioLaRed);

  let dejarDeEscuchar: (() => void) | null = null;
  actual = {
    businessId,
    soltar: () => {
      soltado = true;
      dejarDeEscuchar?.();
      ventana?.removeEventListener("online", cambioLaRed);
      ventana?.removeEventListener("offline", cambioLaRed);
    },
  };
  const esta = actual;

  const escuchar = await deps.pos.onStatus(recibir);
  if (actual !== esta) {
    escuchar();
    return;
  }
  dejarDeEscuchar = escuchar;
  await deps.pos.startHeartbeat({ businessId, bffUrl: deps.bffUrl });
  // Lo que ya sabía el latido antes de escucharlo (otra ruta, otra pantalla).
  recibir(await deps.pos.getStatus());
}

/** Para los tests: arranca de cero. */
export function reiniciarLatidoParaTests() {
  actual?.soltar();
  actual = null;
  useEstadoDeLaCaja.setState(ESTADO_INICIAL, true);
}
